import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { api, setCsrf } from "../api/client.js";

interface AuthState {
  loggedIn: boolean;
  role: string;
  organizationId: string;
  loading: boolean;
}

interface AuthCtx extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ loggedIn: false, role: "", organizationId: "", loading: true });

  useEffect(() => {
    api.get<{ csrfToken: string; role: string; organizationId: string }>("/auth/session")
      .then((s) => {
        setCsrf(s.csrfToken);
        setState({ loggedIn: true, role: s.role, organizationId: s.organizationId, loading: false });
      })
      .catch(() => setState((p) => ({ ...p, loggedIn: false, loading: false })));
  }, []);

  async function login(email: string, password: string) {
    const s = await api.post<{ csrfToken: string; role: string; organizationId: string }>("/auth/login", { email, password });
    setCsrf(s.csrfToken);
    setState({ loggedIn: true, role: s.role, organizationId: s.organizationId, loading: false });
  }

  async function logout() {
    await api.post("/auth/logout").catch(() => undefined);
    setCsrf("");
    setState({ loggedIn: false, role: "", organizationId: "", loading: false });
  }

  return <Ctx.Provider value={{ ...state, login, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
